import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useProfileStore } from '@/store'
import type {
	CreateInteracaoInput,
	Interacao,
	InteracaoAllListParams,
	InteracaoAllListResponse,
	InteracaoListParams,
	InteracaoListResponse,
	InteracaoMeListParams,
	InteracaoMeListResponse,
	InteracaoUpdateInput,
} from '@/types/interacao'
import {
	createInteracao,
	deleteInteracao,
	getInteracaoAllList,
	getInteracaoById,
	getInteracaoList,
	getInteracaoMeList,
	updateInteracao,
} from '../services/interacao'

// --- Queries ---
const QUERY_KEY = ['interacao', 'list']
const ME_QUERY_KEY = ['interacao', 'me', 'list']

export function useInteracaoList(params?: InteracaoListParams) {
	const { defaultCustomerId } = useProfileStore()
	const scopedParams: InteracaoListParams = {
		...params,
		customerId: params?.customerId ?? (defaultCustomerId || undefined),
	}

	return useQuery<InteracaoListResponse>({
		queryKey: [...QUERY_KEY, scopedParams],
		queryFn: () => getInteracaoList(scopedParams),
		staleTime: 5 * 60 * 1000,
	})
}

export function useInteracaoMeList(params?: InteracaoMeListParams) {
	const { defaultBranchId, defaultContractId } = useProfileStore()
	const scopedParams: InteracaoMeListParams = {
		...params,
		scope: params?.scope ?? 'branch',
		activeBranchId: params?.activeBranchId ?? (defaultBranchId || undefined),
		activeCrId: params?.activeCrId ?? (defaultContractId || undefined),
	}

	return useQuery<InteracaoMeListResponse>({
		queryKey: [...ME_QUERY_KEY, scopedParams],
		queryFn: () => getInteracaoMeList(scopedParams),
		staleTime: 5 * 60 * 1000,
	})
}

export function useInteracaoAllList(params?: InteracaoAllListParams) {
	const { defaultBranchId, defaultContractId } = useProfileStore()
	const scopedParams: InteracaoAllListParams = {
		...params,
		scope: params?.scope ?? 'branch',
		activeBranchId: params?.activeBranchId ?? (defaultBranchId || undefined),
		activeCrId: params?.activeCrId ?? (defaultContractId || undefined),
	}

	return useQuery<InteracaoAllListResponse>({
		queryKey: [...ME_QUERY_KEY, scopedParams],
		queryFn: () => getInteracaoAllList(scopedParams),
		staleTime: 5 * 60 * 1000,
	})
}

export function useInteracaoById(id: string) {
	return useQuery<Interacao>({
		queryKey: ['interacao', 'detail', id],
		queryFn: () => getInteracaoById(id),
		staleTime: 5 * 60 * 1000,
		enabled: !!id,
	})
}

// --- Mutations ---

export function useCreateInteracao() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (data: CreateInteracaoInput) => createInteracao(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: QUERY_KEY })
			queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY })
		},
	})
}

export function useUpdateInteracao() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: InteracaoUpdateInput }) =>
			updateInteracao(id, data),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: QUERY_KEY })
			queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY })
			queryClient.invalidateQueries({ queryKey: ['interacao', 'all-list'] })
			queryClient.invalidateQueries({
				queryKey: ['interacao', 'detail', variables.id],
			})
		},
	})
}

export function useDeleteInteracao() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (id: string) => deleteInteracao(id),
		onSuccess: (_data, id) => {
			queryClient.invalidateQueries({ queryKey: QUERY_KEY })
			queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY })
			queryClient.invalidateQueries({ queryKey: ['interacao', 'all-list'] })
			queryClient.invalidateQueries({ queryKey: ['interacao', 'detail', id] })
		},
	})
}
